// Module 1 white-label — derive a full accent scale from one brand primary hex
// and apply it to the --brand-* CSS variables so the whole app re-skins at runtime.
// When no hex is given, we clear the overrides and the stylesheet default (canonical
// Flow teal, in global.css) shows through.

const SCALE_KEYS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

// The given hex is treated as the 500 anchor. Lighter shades mix toward white,
// darker shades toward black — an approximate but coherent ramp for a brand accent.
const TOWARD_WHITE = { 50: 0.92, 100: 0.82, 200: 0.62, 300: 0.42, 400: 0.20 };
const TOWARD_BLACK = { 600: 0.12, 700: 0.30, 800: 0.46, 900: 0.60 };

function hexToRgb(hex) {
    const h = (hex || '').replace('#', '').trim();
    if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(h)) return null;
    const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
    const int = parseInt(full, 16);
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255];
}

function mix([r, g, b], target, w) {
    return [
        Math.round(r + (target - r) * w),
        Math.round(g + (target - g) * w),
        Math.round(b + (target - b) * w)
    ];
}

// Returns { 50: "R G B", … 900: "R G B" } or null if the hex is invalid.
export function deriveScale(hex) {
    const base = hexToRgb(hex);
    if (!base) return null;
    const scale = {};
    [50, 100, 200, 300, 400].forEach((k) => {
        const [r, g, b] = mix(base, 255, TOWARD_WHITE[k]);
        scale[k] = `${r} ${g} ${b}`;
    });
    scale[500] = `${base[0]} ${base[1]} ${base[2]}`;
    [600, 700, 800, 900].forEach((k) => {
        const [r, g, b] = mix(base, 0, TOWARD_BLACK[k]);
        scale[k] = `${r} ${g} ${b}`;
    });
    return scale;
}

// WCAG relative luminance + contrast, and a "walk the ramp until legible" helper — the same
// algorithm BrandingPanel.jsx's live preview already uses (Val, 2026-07-17: "what's dark
// enough to override Black?"). Shared here so applyBrand() can give the RUNNING APP the same
// answer the preview promises, not just the preview itself.
function relLum(r, g, b) {
    [r, g, b] = [r, g, b].map((v) => { v /= 255; return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(channels, hex) {
    if (!channels) return 0;
    const [r, g, b] = channels.split(' ').map(Number);
    const h = hex.replace('#', '');
    const [R, G, B] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    const [l1, l2] = [relLum(r, g, b), relLum(R, G, B)].sort((a, z) => z - a);
    return (l1 + 0.05) / (l2 + 0.05);
}

const DARK_CANVAS = '#0A0E14';
const LIGHT_CANVAS = '#FFFFFF';
const LIGHT_WALK = [800, 700, 900, 600, 500];
const DARK_WALK = [500, 400, 300, 200, 100, 50];

// Each accent role's normal rung per skin (matches global.css, verified AA in
// Coloration_Accent_Roles_v1_00.md §3) and the shared fallback WALK to use only when that
// rung fails 4.5:1 on THIS brand's own canvas — a near-black or near-white custom hex. A
// brand already in use never triggers the walk: its preferred rung already clears AA, so
// this is a safety net, not a redesign of the roles.
const ROLE_PREFERRED = {
    '--btn-fill': { light: 900, dark: 300 },
    '--btn-fill-hover': { light: 900, dark: 300 },
    '--accent-ink': { light: 900, dark: 300 },
    '--accent-ink-hover': { light: 900, dark: 300 },
    '--nav-active-ink': { light: 800, dark: 300 },
    '--nav-active-edge': { light: 600, dark: 500 }
};

function legibleRung(scale, canvasHex, preferred, walk) {
    if (contrast(scale[preferred], canvasHex) >= 4.5) return preferred;
    return walk.find((k) => contrast(scale[k], canvasHex) >= 4.5) ?? walk[walk.length - 1];
}

// Apply (or clear) the brand accent on the document root.
export function applyBrand(hex, isDark = false) {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const scale = deriveScale(hex);
    if (!scale) {
        SCALE_KEYS.forEach((k) => root.style.removeProperty(`--brand-${k}`));
        Object.keys(ROLE_PREFERRED).forEach((v) => root.style.removeProperty(v));
        return;
    }
    SCALE_KEYS.forEach((k) => root.style.setProperty(`--brand-${k}`, scale[k]));

    // THE DARK-BRAND ANSWER, now live — previously only BrandingPanel.jsx's preview did this
    // (Val, 2026-07-17: "what's dark enough to override Black?"). A normal brand's preferred
    // rung already clears AA (verified in Coloration_Accent_Roles_v1_00.md §3), so this is a
    // no-op for every brand already in use — it only overrides a role when this brand's own
    // rung fails 4.5:1 against its own canvas.
    const canvas = isDark ? DARK_CANVAS : LIGHT_CANVAS;
    const walk = isDark ? DARK_WALK : LIGHT_WALK;
    Object.entries(ROLE_PREFERRED).forEach(([cssVar, bySkin]) => {
        const preferred = isDark ? bySkin.dark : bySkin.light;
        const rung = legibleRung(scale, canvas, preferred, walk);
        root.style.setProperty(cssVar, `rgb(${scale[rung]})`);
    });
}
